import type { SupabaseClient } from "@supabase/supabase-js";
import type { ItemType } from "../captures/items-schema";

export interface AuditFlag {
  description: string;
  // ADR 0013 §2 / Lot 8 : id de l'item visé (null si juge n'a pas pu relier
  // ou si flag = "missed"). Permet à l'UI d'afficher un badge ⚠ ciblé par
  // item (QualityBadge / `needs_review`) côté capture.
  item_id?: string | null;
  // ADR 0017 : correction proposée par le juge (optionnelle), portée par le
  // jsonb `flags`. typing_errors → `corrected_type` ; hallucinated →
  // `suggested_action` (fix_text/delete) + `suggested_text` (si fix_text).
  corrected_type?: ItemType;
  suggested_action?: "fix_text" | "delete";
  suggested_text?: string;
}

export interface AuditScorecard {
  id: string;
  capture_id: string;
  capture_title: string | null;
  prompt_version: string;
  judge_model: string;
  scores: {
    fidelity: number;
    typing: number;
    speakers: number;
    coverage: number;
    citation_self_sufficiency: number;
    redundancy: number;
  };
  coverage_pct: number | null;
  n_items: number;
  flags: {
    missed: AuditFlag[];
    hallucinated: AuditFlag[];
    typing_errors: AuditFlag[];
  };
  verdict: string;
  created_at: string;
}

// ADR 0017 — correction proposée par le juge pour UN item, agrégée depuis ses
// flags (typing_errors → corrected_type ; hallucinated → suggested_action/text).
export interface ItemCorrection {
  corrected_type?: ItemType;
  suggested_action?: "fix_text" | "delete";
  suggested_text?: string;
}

// Indexe les corrections proposées par item_id, pour les afficher et les
// appliquer au niveau de chaque item (CaptureScreen → ItemBlock).
export function correctionsByItemId(audit: AuditScorecard | null): Map<string, ItemCorrection> {
  const map = new Map<string, ItemCorrection>();
  if (!audit) return map;
  for (const f of audit.flags.typing_errors ?? []) {
    if (f.item_id && f.corrected_type) {
      map.set(f.item_id, { ...map.get(f.item_id), corrected_type: f.corrected_type });
    }
  }
  for (const f of audit.flags.hallucinated ?? []) {
    if (f.item_id && f.suggested_action) {
      map.set(f.item_id, {
        ...map.get(f.item_id),
        suggested_action: f.suggested_action,
        suggested_text: f.suggested_text,
      });
    }
  }
  return map;
}

type FetchResult = { ok: true; data: AuditScorecard[] } | { ok: false; error: string };
type FetchOneResult = { ok: true; data: AuditScorecard | null } | { ok: false; error: string };
type FetchBatchResult =
  | {
      ok: true;
      data: Map<string, CaptureAuditSummary>;
      /** La photo est-elle ENTIÈRE ? `false` = réponse partielle : une capture
       *  absente de la Map n'est PAS une capture sans audit, c'est une capture
       *  sur laquelle on ne sait rien. */
      complete: boolean;
    }
  | { ok: false; error: string };

const DEFAULT_LIMIT = 50;

// Projection MINIMALE du batch d'accueil : la liste ne se sert de l'audit que
// pour le badge « N à corriger » (countToCorrect → `flags.hallucinated[].item_id`).
// Ni `scores`, ni `verdict`, ni la jointure `captures(title)` — inutiles ici et
// coûteux en egress (ligne complète ≈ 2,8 ko en prod, dont ≈ 1,9 ko de `flags`).
export const AUDIT_SUMMARY_COLUMNS = "id, capture_id, created_at, flags";

// Taille de page demandée, volontairement sous le plafond serveur (1000).
export const AUDITS_PAGE_SIZE = 500;
// Garde-fou anti-boucle : un serveur qui ne progresse pas ne doit pas figer
// l'accueil. Atteindre ce plafond ⇒ `complete: false`, jamais un faux
// « capture jamais auditée ».
export const AUDITS_MAX_PAGES = 40;

// Vue allégée d'un audit, suffisante pour le badge de la liste d'accueil.
// `AuditScorecard` la satisfait structurellement : la page dédiée (qui charge
// la scorecard entière) peut alimenter les mêmes consommateurs sans conversion.
export interface CaptureAuditSummary {
  id: string;
  capture_id: string;
  created_at: string;
  flags: AuditScorecard["flags"];
}

function mapSummaryRow(row: Record<string, unknown>): CaptureAuditSummary | null {
  if (typeof row.id !== "string" || typeof row.capture_id !== "string") return null;
  return {
    id: row.id,
    capture_id: row.capture_id,
    created_at: String(row.created_at ?? ""),
    flags: (row.flags ?? {}) as AuditScorecard["flags"],
  };
}

// Range une page de lignes dans l'accumulateur « dernier audit par capture ».
// Les lignes arrivent triées created_at DESC : la PREMIÈRE vue pour une capture
// est la plus récente, les suivantes sont ignorées — y compris quand elles
// arrivent dans une page ultérieure (la dédup tient par-dessus la coupure).
// Une ligne malformée est ignorée plutôt que de faire planter le rendu.
function keepLatestPerCapture(
  rows: Record<string, unknown>[],
  byCaptureId: Map<string, CaptureAuditSummary>,
): void {
  for (const row of rows) {
    const audit = mapSummaryRow(row);
    if (!audit) continue;
    if (!byCaptureId.has(audit.capture_id)) byCaptureId.set(audit.capture_id, audit);
  }
}

function mapRow(row: Record<string, unknown>): AuditScorecard {
  const cap = row.captures as { title?: string | null } | null;
  return {
    id: String(row.id),
    capture_id: String(row.capture_id),
    capture_title: cap?.title ?? null,
    prompt_version: String(row.prompt_version),
    judge_model: String(row.judge_model),
    scores: row.scores as AuditScorecard["scores"],
    coverage_pct: row.coverage_pct === null ? null : Number(row.coverage_pct),
    n_items: Number(row.n_items),
    flags: (row.flags ?? {}) as AuditScorecard["flags"],
    verdict: String(row.verdict ?? ""),
    created_at: String(row.created_at),
  };
}

// Charge la scorecard la plus récente pour une capture donnée (Lot 10 / ADR
// 0014 §2). Renvoie `null` si aucun audit n'a encore été produit, ce qui
// permet à l'UI d'afficher un CTA « Lancer un audit » plutôt qu'une erreur.
export async function fetchAuditForCapture(
  client: SupabaseClient,
  captureId: string,
): Promise<FetchOneResult> {
  const { data, error } = await client
    .from("capture_audits")
    .select(
      "id, capture_id, prompt_version, judge_model, scores, coverage_pct, n_items, flags, verdict, created_at, captures(title)",
    )
    .eq("capture_id", captureId)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) return { ok: false, error: error.message };
  const rows = (data ?? []) as Record<string, unknown>[];
  const first = rows[0];
  if (!first) return { ok: true, data: null };
  return { ok: true, data: mapRow(first) };
}

// Liste les scorecards récentes (RLS-bornées : visibles à qui peut voir la
// capture sous-jacente — politique capture_audits_read).
//
// `captureId` (ADR 0014 §2) : quand fourni via deep-link `/audits?captureId=`,
// filtre côté serveur pour ne renvoyer que les scorecards de cette capture
// (sinon le cap de `limit` peut faire disparaître une capture plus ancienne).
export async function fetchRecentAudits(
  client: SupabaseClient,
  options: { limit?: number; captureId?: string } = {},
): Promise<FetchResult> {
  const { limit = DEFAULT_LIMIT, captureId } = options;
  let query = client
    .from("capture_audits")
    .select(
      "id, capture_id, prompt_version, judge_model, scores, coverage_pct, n_items, flags, verdict, created_at, captures(title)",
    );
  if (captureId) query = query.eq("capture_id", captureId);
  const { data, error } = await query.order("created_at", { ascending: false }).limit(limit);
  if (error) return { ok: false, error: error.message };
  const rows = (data ?? []) as unknown[];
  return {
    ok: true,
    data: rows.map((r) => mapRow(r as Record<string, unknown>)),
  };
}

// Charge le plus récent audit par capture, pour PLUSIEURS captures en UNE
// SEULE requête (perf accueil 22-07 — évite le N+1 : ~30 requêtes au mount de
// l'accueil pour un admin). Même patron que fetchItemsForCaptures (.in()) :
// une capture jamais auditée est simplement ABSENTE de la Map retournée (pas
// d'entrée `null`) — au composant appelant de retomber sur `.get(id) ?? null`.
//
// ⚠ PostgREST plafonne toute réponse à 1000 lignes SANS le dire (statut 200,
// tableau partiel, en-tête `content-range: 0-999/*`). Cette requête lisait TOUS
// les audits des captures prêtes d'un coup : 115 lignes en prod aujourd'hui,
// mais chaque ré-audit en ajoute une (déjà 11 pour une même capture). Passé le
// plafond, les captures dont les audits tombent après la coupure perdraient
// leur badge « N à corriger » sans le moindre message — exactement le défaut
// corrigé le 2026-09-22 sur les items (cf. captures/items-fetch.ts).
//
// Deux réponses, indissociables :
//   1. paginer par `range()` jusqu'à ÉPUISEMENT (plus de coupure) ;
//   2. lire le total renvoyé par le serveur (`count` = le `content-range`) et
//      dire explicitement, via `complete`, si la photo est entière. Une photo
//      partielle vaut « je ne sais pas », jamais « pas d'audit ».
export async function fetchAuditsForCaptures(
  client: SupabaseClient,
  captureIds: string[],
): Promise<FetchBatchResult> {
  if (captureIds.length === 0) return { ok: true, data: new Map(), complete: true };

  const byCaptureId = new Map<string, CaptureAuditSummary>();
  let collected = 0;
  // Total annoncé par le serveur (dérivé de `content-range` par supabase-js).
  // `null` tant qu'aucune réponse ne l'a porté.
  let serverTotal: number | null = null;
  let exhausted = false;

  for (let page = 0; page < AUDITS_MAX_PAGES; page++) {
    const from = collected;
    // Ordre TOTAL et déterministe. `created_at` seul ne suffit pas : deux
    // audits de la même capture peuvent partager l'horodatage (un ré-audit en
    // rafale), et deux lignes ex æquo peuvent alors permuter d'une page à
    // l'autre — doublon ici, ligne jamais servie là. `id` (uuid, clé primaire,
    // NOT NULL) tranche. DESC sur les deux : la PREMIÈRE ligne vue pour une
    // capture est son audit le plus récent, ce qui fait tenir la dédup
    // ci-dessous même par-dessus une coupure de page.
    const { data, error, count } = await client
      .from("capture_audits")
      .select(AUDIT_SUMMARY_COLUMNS, { count: "exact" })
      .in("capture_id", captureIds)
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .range(from, from + AUDITS_PAGE_SIZE - 1);

    if (error) return { ok: false, error: error.message };
    if (typeof count === "number") serverTotal = count;

    const rows = (data ?? []) as Record<string, unknown>[];
    keepLatestPerCapture(rows, byCaptureId);
    collected += rows.length;

    // Page vide = il n'y a plus rien à lire. C'est la SEULE preuve d'épuisement
    // qui ne dépende pas de la taille de page : une page plus courte que
    // demandée signifie simplement que le serveur plafonne plus bas que nous,
    // pas qu'on a tout vu (c'est exactement le piège d'origine).
    if (rows.length === 0) {
      exhausted = true;
      break;
    }
    if (serverTotal !== null && collected >= serverTotal) {
      exhausted = true;
      break;
    }
  }

  const complete = serverTotal !== null ? collected >= serverTotal : exhausted;
  return { ok: true, data: byCaptureId, complete };
}

// Fusionne une NOUVELLE photo d'audits dans celle déjà affichée.
//
// Photo ENTIÈRE : elle fait autorité — une capture qui n'y figure plus n'a
// réellement pas d'audit, elle sort de la Map.
// Photo PARTIELLE : une capture absente ne prouve rien. On ne lit pas une
// troncature comme une absence : ce que l'on savait déjà est conservé, et la
// nouvelle photo ne peut qu'ajouter ou rafraîchir.
export function mergeAuditPhoto(
  prev: Map<string, CaptureAuditSummary>,
  next: Map<string, CaptureAuditSummary>,
  complete: boolean,
): Map<string, CaptureAuditSummary> {
  if (complete) return next;
  return new Map([...prev, ...next]);
}
